import * as THREE from "three";
import { Kit, ethMark, instance, revolve, type V2 } from "./kit";
import { blobShadow, glowSprite, printMaterial } from "./fx";
import { stripesTex } from "./tex";

/** ETH on the road: a hazard. A black coin with a glowing red ring and a red crystal glyph, on a striped base. */

const R = 0.85;
const CY = 1.0;          // coin centre: bottom 0.15, top 1.85 (the entity is 1.85 tall)
const SEG = 32;

/** The red of the glyph and rim: lit, and glowing in its own colour. */
const RED = { r: 0.28, m: 0.25, e: 0.9 };

/**
 * `buildEthCoin()` → origin = the road under the coin (scene sets y = yBase), coin faces ±z, child "spin" wobbles
 * about y, child "lamps" blink. ~900 triangles.
 */
export function buildEthCoin(): THREE.Group {
  return instance("ethCoin", () => {
    const body = new Kit();
    const face: V2[] = [[0, 0.09], [0.62, 0.09]];
    const lipIn: V2[] = [[0.62, 0.09], [0.66, 0.13]];
    const rim: V2[] = [[0.66, 0.13], [0.77, 0.13]];
    const edge: V2[] = [[0.77, 0.13], [R, 0.07], [R, -0.07], [0.77, -0.13]];
    const mirror = (pts: V2[]): V2[] => pts.map(([r, z]) => [r, -z] as V2).reverse();
    for (const pts of [face, mirror(face)]) body.add(revolve(pts, SEG), "satin", 0x1a1a22);
    for (const pts of [lipIn, mirror(lipIn)]) body.add(revolve(pts, SEG), "satin", 0x2a2a34);
    body.add(revolve(edge, SEG), "satin", 0x24242c);
    for (const pts of [rim, mirror(rim)]) body.add(revolve(pts, SEG), RED, 0xff2440);
    // the glyph, raised on both faces; the band gets a thin red light line
    const glyph = ethMark(1.0, 0.07);
    body.add(glyph, RED, 0xff2440, { p: [0, 0, 0.088] });
    body.add(glyph, RED, 0xff2440, { p: [0, 0, -0.088], r: [0, Math.PI, 0] });
    body.add(revolve([[R + 0.004, 0.018], [R + 0.004, -0.018]], SEG), "glow", new THREE.Color(0xff2a48).multiplyScalar(1.8));
    const spin = body.build({ name: "spin" });
    spin.position.y = CY;

    // the base: a low drum with hazard stripes round its skirt and a red light ring on top
    const base = new Kit();
    const stripes = printMaterial("ethBaseStripes", stripesTex("#ffc21a", "#15151b", 10, 512, 64), { roughness: 0.55 });
    base.add(revolve([[0.5, 0.12], [0.56, 0.0]], 24, { axis: "y" }), stripes);
    base.add(revolve([[0, 0.14], [0.4, 0.14], [0.5, 0.12]], 24, { axis: "y" }), "rubber", 0x1d1d24);
    base.add(revolve([[0.41, 0.146], [0.47, 0.132]], 24, { axis: "y" }), "glow", new THREE.Color(0xff2440).multiplyScalar(1.6));
    base.add(new THREE.CylinderGeometry(0.07, 0.09, 0.08, 10, 1, true), "metal", 0x2b2b33, { p: [0, 0.17, 0] });
    const stand = base.build({ name: "base" });

    const root = new THREE.Group();
    root.name = "ethCoin";
    root.userData.anim = "ethCoin";
    const glow = glowSprite(0xff2040, 2.9, 0.6, 0.9);
    glow.position.y = CY;
    root.add(blobShadow(2.0, 1.6), stand, glow, spin);
    return root;
  });
}
